/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import Text from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import styles from "./styles";
import { useDispatch } from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTranslation } from "react-i18next";
import axios from "api/configApi";
import dayjs from "dayjs";
import { toBlob } from "html-to-image";
import badgeImg from "asset/img/badge.jpg";
import badgeInactiveImg from "asset/img/badge-inactive.jpg";
import { Badge } from "@shelter/core/dist/models";
import { SET_MESSAGES_REDUCER } from "redux/reducers/messages/actionTypes";

interface BadgesTabProps {
  /** When set (e.g. Manage Volunteers Work), loads badges for that user. */
  userId?: string;
}

/** Output file size (square) for Download. */
const DOWNLOAD_IMAGE_SIZE_PX = 1024;
/**
 * Longest edge of the captured PNG (device pixels). Use pixelRatio only — setting width/height
 * on toBlob() inflates the SVG but foreignObject content stays small (top-left), causing a huge white mat.
 */
const CAPTURE_MAX_EDGE_PX = 1280;

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to decode image"));
    };
    img.src = url;
  });
}

async function renderBlobToSquarePng(
  sourceBlob: Blob,
  sizePx: number
): Promise<Blob> {
  const img = await blobToImage(sourceBlob);
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const canvas = document.createElement("canvas");
  canvas.width = sizePx;
  canvas.height = sizePx;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D unavailable");
  }
  const scale = Math.min(sizePx / iw, sizePx / ih);
  const dw = Math.round(iw * scale);
  const dh = Math.round(ih * scale);
  const dx = Math.round((sizePx - dw) / 2);
  const dy = Math.round((sizePx - dh) / 2);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, iw, ih, dx, dy, dw, dh);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/png"
    );
  });
}

const BadgesTab = React.memo((props: BadgesTabProps) => {
  const { userId } = props;
  const classes = styles();
  const translate = useTranslation().t;
  const dispatch = useDispatch();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
  const [sharePayload, setSharePayload] = useState<{
    blob: Blob;
    message: string;
    file: File;
    badgeName: string;
  } | null>(null);
  const badgeWrapRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBadges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchBadges = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/users/badges-history", {
        params: userId ? { userId } : {},
      });
      setBadges(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load badges", error);
      setBadges([]);
    } finally {
      setLoading(false);
    }
  };

  const showDownloadedToast = () =>
    dispatch({
      type: SET_MESSAGES_REDUCER,
      message: { message: translate("BADGE_IMAGE_DOWNLOADED"), type: "success" },
    });

  const getShareMessage = (badgeName: string) =>
    translate("SHARE_BADGE_MESSAGE", { badgeName });

  const isMobileWeb = () =>
    typeof navigator !== "undefined" &&
    (navigator.maxTouchPoints > 0 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));

  /**
   * Waits for all images inside the element to finish loading and decoding
   * before resolving. Ensures the DOM is ready for html-to-image toBlob()
   * so the captured canvas includes the actual badge graphic (avoids blank
   * or missing image on first share, especially on mobile).
   */
  const waitForImages = (element: HTMLElement): Promise<void> => {
    const imgs = element.querySelectorAll("img");
    const loadPromises = Array.from(imgs).map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    );
    return Promise.all(loadPromises).then(() => {
      const decodePromises = Array.from(imgs).map((img) =>
        typeof img.decode === "function" ? img.decode().catch(() => {}) : Promise.resolve()
      );
      return Promise.all(decodePromises).then(() => {});
    });
  };

  const handleShare = async (badge: Badge, index: number) => {
    const node = badgeWrapRefs.current[index];
    if (!node) return;
    try {
      await waitForImages(node);
      const w = node.offsetWidth;
      const h = node.offsetHeight;
      const maxDim = Math.max(w, h, 1);
      const pixelRatio = CAPTURE_MAX_EDGE_PX / maxDim;
      const blob = await toBlob(node, {
        pixelRatio,
        cacheBust: true,
        backgroundColor: "#ffffff",
      });
      if (!blob) return;

      const message = getShareMessage(badge.badgeName);
      const file = new File([blob], `[ShelterApp] ${badge.badgeName.replace(/\s+/g, "-")}.png`, {
        type: "image/png",
      });

      if (isMobileWeb() && navigator.share) {
        await navigator.share({
          title: badge.badgeName,
          text: message,
          files: [file],
        } as ShareData & { files?: File[] });
      } else {
        setSharePayload({ blob, message, file, badgeName: badge.badgeName });
        setShareDialogOpen(true);
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
    }
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setSharePayload(null);
  };

  const handleNativeShare = async () => {
    if (!sharePayload) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: sharePayload.badgeName,
          text: sharePayload.message,
          files: [sharePayload.file],
        } as ShareData & { files?: File[] });
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        // ignore
      }
    }
    handleShareDialogClose();
  };

  const handleDownloadImage = async () => {
    if (!sharePayload) return;
    const png1024 = await renderBlobToSquarePng(
      sharePayload.blob,
      DOWNLOAD_IMAGE_SIZE_PX
    );
    const url = URL.createObjectURL(png1024);
    const a = document.createElement("a");
    a.href = url;
    a.download = sharePayload.file.name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showDownloadedToast();
    handleShareDialogClose();
  };

  const renderBadges = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 24 }}>
          <CircularProgress />
        </div>
      );
    }

    if (!loading && badges.length === 0) {
      return (
        <p>
          <i>{translate("NO_BADGES_YET")}</i>
        </p>
      );
    }

    return (
      <div className={classes.grid}>
        {badges.map((badge, index) => {
          const src = badge.isActive ? badgeImg : badgeInactiveImg;
          return (
            <div key={badge.id ?? index} className={classes.card}>
              <div
                className={classes.badgeImageWrap}
                ref={(el) => {
                  if (el) badgeWrapRefs.current[index] = el;
                }}
              >
                <img
                  src={src}
                  alt={badge.badgeName}
                  className={classes.badgeImage}
                />
                <div className={classes.badgeOverlay}>
                  <Text
                    variant="body2"
                    className={`${classes.badgeNameOverlay} ${badge.badgeName.length > 18 ? classes.badgeNameOverlayLong : ""}`}
                  >
                    {badge.badgeName}
                  </Text>
                  <div className={classes.badgeBoxOverlay}></div>
                  {badge.isActive && badge.achievedAt && (
                    <Text variant="caption" className={classes.badgeYearOverlay}>
                      {new Date().getFullYear()}
                    </Text>
                  )}
                </div>
              </div>
              <div className={classes.meta}>
                <Text
                  variant="body2"
                  className={badge.isActive ? classes.metaText : classes.metaTextInactive}
                >
                  {badge.points} {translate("POINTS")}
                </Text>
                <Text
                  variant="body2"
                  className={badge.isActive ? classes.metaText : classes.metaTextInactive}
                >
                  {badge.isActive && badge.achievedAt
                    ? translate("ACHIEVED_ON", {
                        date: dayjs(badge.achievedAt).format("MMM D, YYYY"),
                      })
                    : translate("NOT_ACHIEVED")}
                </Text>
                {badge.isActive && (
                  <button
                    type="button"
                    className={classes.shareButton}
                    onClick={() => handleShare(badge, index)}
                  >
                    {translate("SHARE")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Container className={classes.root}>
        {renderBadges()}
      </Container>
      <Dialog open={shareDialogOpen} onClose={handleShareDialogClose}>
        <DialogContent>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16 }}>
            {navigator.share && (
              <Button onClick={handleNativeShare} color="primary" variant="outlined">
                {translate("SHARE")}
              </Button>
            )}
            <Button onClick={handleDownloadImage} color="primary" variant="outlined">
              {translate("DOWNLOAD_IMAGE")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default BadgesTab;
